import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { Ticket, Treatment, Wallet } from '@/lib/types';
import { PageHeader, Pill, cellStyle, fieldStyle, thStyle } from '@/components/ui';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { useSort, SortTh } from '@/components/sortable';
import { newKey, ntd } from '@/lib/format';

export function BillingPage() {
  const qc = useQueryClient();
  // 自其他頁(顧客主頁 / 今日看板)帶入預選顧客
  const [searchParams] = useSearchParams();
  const [customerId, setCustomerId] = useState(searchParams.get('customerId') ?? '');
  const [pickerLabel] = useState(searchParams.get('name') ?? '');
  const [amount, setAmount] = useState(1000);
  const [payMethod, setPayMethod] = useState('CASH');
  const [lastWalletTxn, setLastWalletTxn] = useState('');
  const [lastTicketTxn, setLastTicketTxn] = useState('');
  const [grantTreatmentId, setGrantTreatmentId] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null,
  );

  const treatments = useQuery({
    queryKey: ['treatments'],
    queryFn: () => api.get<Treatment[]>('/catalog/treatments'),
  });
  const wallet = useQuery({
    queryKey: ['wallet', customerId],
    queryFn: () => api.get<Wallet>(`/billing/wallet/${customerId}`),
    enabled: !!customerId,
  });
  const tickets = useQuery({
    queryKey: ['tickets', customerId],
    queryFn: () => api.get<Ticket[]>(`/billing/tickets/${customerId}`),
    enabled: !!customerId,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['wallet', customerId] });
    void qc.invalidateQueries({ queryKey: ['tickets', customerId] });
  };
  const ok = (text: string) => {
    setMsg({ kind: 'ok', text });
    refresh();
  };
  const fail = (e: unknown) =>
    setMsg({ kind: 'err', text: e instanceof ApiError ? e.message : '操作失敗' });

  const deposit = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/billing/wallet/deposit', {
        customerId,
        amountCents: amount * 100,
        payMethod,
        idempotencyKey: newKey(),
      }),
    onSuccess: () => ok('儲值成功'),
    onError: fail,
  });
  const consume = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/billing/wallet/consume', {
        customerId,
        amountCents: amount * 100,
        idempotencyKey: newKey(),
      }),
    onSuccess: (r) => {
      setLastWalletTxn(r.id);
      ok('核銷成功');
    },
    onError: fail,
  });
  const refundWallet = useMutation({
    mutationFn: () =>
      api.post('/billing/wallet/refund', {
        transactionId: lastWalletTxn,
        idempotencyKey: newKey(),
      }),
    onSuccess: () => ok('退款成功，餘額已回補'),
    onError: fail,
  });

  const grant = useMutation({
    mutationFn: (treatmentId: string) =>
      api.post('/billing/tickets/grant', {
        customerId,
        treatmentId,
        total: 5,
        idempotencyKey: newKey(),
      }),
    onSuccess: () => ok('已發放 5 堂票券'),
    onError: fail,
  });
  const consumeTicket = useMutation({
    mutationFn: (ticketId: string) =>
      api.post<{ id: string }>('/billing/tickets/consume', {
        ticketId,
        count: 1,
        idempotencyKey: newKey(),
      }),
    onSuccess: (r) => {
      setLastTicketTxn(r.id);
      ok('核銷 1 堂');
    },
    onError: fail,
  });
  const refundTicket = useMutation({
    mutationFn: () =>
      api.post('/billing/tickets/refund', {
        transactionId: lastTicketTxn,
        idempotencyKey: newKey(),
      }),
    onSuccess: () => ok('已退回 1 堂'),
    onError: fail,
  });

  const sortTickets = useSort(tickets.data ?? [], {
    treatment: (t) => treatments.data?.find((x) => x.id === t.treatmentId)?.name ?? t.treatmentId,
    remaining: (t) => t.remaining,
  });

  return (
    <div>
      <PageHeader
        title="儲值金 / 堂票"
        subtitle="FOR UPDATE 行鎖 + 冪等鍵；餘額不足整筆 ROLLBACK；退款逆向回補"
      />

      <div style={{ marginBottom: 16, maxWidth: 320 }}>
        <CustomerPicker
          value={customerId}
          onChange={(id) => setCustomerId(id)}
          initialLabel={pickerLabel}
        />
      </div>

      {msg && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 'var(--r-md)',
            marginBottom: 16,
            background: msg.kind === 'ok' ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: msg.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
            fontSize: 13,
          }}
        >
          {msg.text}
        </div>
      )}

      {!customerId && (
        <div
          className="card"
          style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--ink-3)' }}
        >
          <div style={{ fontSize: 32, marginBottom: 10 }}>💳</div>
          <div style={{ fontSize: 15, color: 'var(--ink-2)', marginBottom: 6 }}>
            請先於上方搜尋並選擇顧客
          </div>
          <div style={{ fontSize: 13 }}>
            選定後即可檢視該顧客的儲值金餘額、累計儲值,並進行
            <b>儲值 / 核銷 / 退款</b>,以及<b>堂票發放 / 核銷 / 退回</b>。
          </div>
        </div>
      )}

      {customerId && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div className="card" style={{ padding: 18 }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--wallet)' }}>儲值金</h3>
            <div className="num" style={{ fontSize: 28, fontWeight: 700, color: 'var(--ink)' }}>
              {wallet.data ? ntd(wallet.data.balanceCents) : '—'}
            </div>
            <div style={{ color: 'var(--ink-4)', fontSize: 12, marginBottom: 16 }}>
              累計儲值 {wallet.data ? ntd(wallet.data.depositedCents) : '—'}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>金額 NT$</span>
              <input
                type="number"
                className="num"
                style={{ ...fieldStyle, width: 110 }}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>付款方式</span>
              {[
                { v: 'CASH', l: '現金' },
                { v: 'TRANSFER', l: '轉帳' },
                { v: 'CARD', l: '刷卡' },
                { v: 'OTHER', l: '其他' },
              ].map((m) => (
                <button
                  key={m.v}
                  className={payMethod === m.v ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => setPayMethod(m.v)}
                >
                  {m.l}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={deposit.isPending} onClick={() => deposit.mutate()}>
                儲值
              </button>
              <button className="btn btn-ghost" disabled={consume.isPending} onClick={() => consume.mutate()}>
                核銷
              </button>
              <button className="btn btn-ghost" disabled={!lastWalletTxn || refundWallet.isPending} onClick={() => refundWallet.mutate()}>
                退款（最近核銷）
              </button>
            </div>
          </div>

          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: 'var(--ticket)' }}>堂票</h3>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Select
                  value={grantTreatmentId}
                  onChange={(val) => setGrantTreatmentId(val)}
                  placeholder="發放療程…"
                  options={(treatments.data ?? []).map((t) => ({ value: t.id, label: `${t.name}` }))}
                />
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    if (grantTreatmentId) grant.mutate(grantTreatmentId);
                  }}
                >
                  發放 5 堂
                </button>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <SortTh label="療程" sortKey="treatment" sort={sortTickets} />
                  <SortTh label="剩餘 / 總數" sortKey="remaining" sort={sortTickets} />
                  <th style={thStyle}>操作</th>
                </tr>
              </thead>
              <tbody>
                {sortTickets.sorted.map((t) => {
                  const tr = treatments.data?.find((x) => x.id === t.treatmentId);
                  return (
                    <tr key={t.id}>
                      <td style={cellStyle}>{tr?.name ?? t.treatmentId}</td>
                      <td style={cellStyle}>
                        <Pill bg="var(--ticket-bg)" color="var(--ticket)">
                          {t.remaining} / {t.total}
                        </Pill>
                      </td>
                      <td style={{ ...cellStyle, display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost" disabled={t.remaining <= 0} onClick={() => consumeTicket.mutate(t.id)}>
                          核銷 1
                        </button>
                        <button className="btn btn-ghost" disabled={!lastTicketTxn} onClick={() => refundTicket.mutate()}>
                          退回（最近）
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {tickets.data?.length === 0 && (
                  <tr>
                    <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={3}>
                      尚無堂票
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
